import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { AddressSchema } from '@/types/common-types';
import { useQuery } from '@tanstack/react-query';
import {
  FetchDeviceQuery,
  FetchSubDevicesQuery,
  UpdateDeviceInput,
} from '@/lib/api/devices/devices';

const DeviceFormSchema = z.object({
  name: z.string().optional(),
  nvrId: z.string().optional(),
  description: z.string().optional(),
  address: AddressSchema.optional(),
  connectionSettings: z.record(z.unknown()).optional(),
  monitoredDevices: z.record(z.boolean()),
  integrationId: z.string(),
  customerId: z.string(),
  externalConnectionChoice: z.record(z.unknown()).optional(),
  routeNamespaceId: z.string().optional(),
  deviceCreationChoices: z.record(z.string()).optional(),
});

type DeviceFormValues = z.infer<typeof DeviceFormSchema>;

export const DeviceFormProvider = (props: PropsWithChildren<{ deviceId: string }>) => {
  const { deviceId } = props;

  const form = useForm<DeviceFormValues>({
    resolver: zodResolver(DeviceFormSchema),
    defaultValues: {
      name: '',
      nvrId: '',
      description: '',
      address: undefined,
      connectionSettings: undefined,
      monitoredDevices: {},
      integrationId: '',
      customerId: '',
      routeNamespaceId: '',
    },
  });

  // FETCHING
  const deviceQuery = useQuery(FetchDeviceQuery({ deviceId }));
  const device = deviceQuery.data;
  const subDevicesQuery = useQuery({
    ...FetchSubDevicesQuery({ deviceId: deviceId ?? '' }),
    enabled: deviceId != null,
  });
  const subDevices = subDevicesQuery.data;

  useEffect(() => {
    const monitored: Record<string, boolean> = {};

    for (const sub of subDevices ?? []) {
      if (sub.id == null || !sub.monitored) continue;
      monitored[sub.id] = true;
    }

    form.reset(
      {
        name: device?.name ?? '',
        nvrId: device?.nvrId ?? '',
        description: device?.description ?? '',
        address: device?.address ?? undefined,
        connectionSettings: device?.integrationKeyValue ?? undefined,
        integrationId: device?.integration?.id ?? '',
        customerId: device?.customerId ?? '',
        monitoredDevices: monitored,
        routeNamespaceId: device?.routeNamespaceId ?? '',
        deviceCreationChoices: device?.deviceCreationChoices ?? undefined,
      },
      { keepDirtyValues: true }
    );
  }, [device, subDevices]);

  return <FormProvider {...form}>{props.children}</FormProvider>;
};

export const useDeviceFormCtx = () => useFormContext<DeviceFormValues>();

export const useFormatUpdateDeviceInput = (deviceId: string) => {
  const subDevicesQuery = useQuery({
    ...FetchSubDevicesQuery({ deviceId }),
  });
  const subDevices = subDevicesQuery.data;

  return useCallback(
    (data: DeviceFormValues): Omit<UpdateDeviceInput, 'deviceId'> | undefined => {
      if (subDevices == null) return;

      const monitoredDevices = subDevices.map((device) => ({
        id: device.id ?? '',
        monitored: device.id != null && data.monitoredDevices[device.id],
        description: device.description,
        quillNotes: device.quillNotes,
      }));

      return {
        nvr: {
          externalId: data.nvrId,
          description: data.description || '',
          name: data.name,
        },
        connectionSettings: data.connectionSettings || {},
        monitoredDevices: monitoredDevices,
        integrationId: data.integrationId,
        customerId: data.customerId,
        address: data.address ?? {},
        routeNamespaceId: data.routeNamespaceId || null,
        deviceCreationChoices: data.deviceCreationChoices || null,
      };
    },
    [subDevices]
  );
};
